// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3H: Item Set components viewer.
//
// Read-only drawer fired from the Item Master grid's context menu
// ("View Components"). Shows the parent set metadata + a slim table
// of components. Save / Edit happens through the Hardware popup
// itself — this drawer is purely informational so an operator can
// inspect a set without going through the edit flow.
// ─────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { Drawer, Tag, Table, Alert, Skeleton, Empty, Button, Space } from 'antd';
import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import { itemsApi } from '../itemsApi';

export default function ItemSetComponentsDrawer({
  open, recordId, recordLabel, onClose, onEdit
}) {
  const [loading, setLoading] = useState(false);
  const [set, setSet]         = useState(null);
  const [components, setComponents] = useState([]);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!open || !recordId) { setSet(null); setComponents([]); setError(null); return undefined; }
    setLoading(true); setError(null);
    itemsApi.getItemSet(recordId)
      .then((data) => {
        if (cancelled) return;
        if (!data) { setSet(null); setComponents([]); return; }
        setSet(data.set || null);
        setComponents(Array.isArray(data.components) ? data.components : []);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.data?.error?.message || e?.message
            || 'Failed to load set components.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, recordId]);

  const columns = [
    {
      title: '#',
      dataIndex: 'sequenceNo',
      key: 'sequenceNo',
      width: 40,
      render: (v, _r, i) => v != null ? v : (i + 1)
    },
    {
      title: 'Component',
      dataIndex: 'componentItemName',
      key: 'componentItemName',
      render: (v, row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{v || row.componentNameSnapshot || '—'}</div>
          {row.componentItemCode ? (
            <code style={{ fontSize: 11, color: '#64748b' }}>{row.componentItemCode}</code>
          ) : null}
        </div>
      )
    },
    {
      title: 'Qty',
      dataIndex: 'qty',
      key: 'qty',
      width: 80,
      align: 'right',
      render: (v) => v != null ? Number(v) : '—'
    },
    {
      title: 'UOM',
      dataIndex: 'uomCode',
      key: 'uomCode',
      width: 70,
      render: (v) => v
        ? <code style={{ fontSize: 11, color: '#475569' }}>{v}</code>
        : <span style={{ color: '#94a3b8' }}>—</span>
    },
    {
      title: 'Optional',
      dataIndex: 'isOptional',
      key: 'isOptional',
      width: 80,
      align: 'center',
      render: (v) => v
        ? <Tag color="default" style={{ fontSize: 10, margin: 0 }}>Optional</Tag>
        : <span style={{ color: '#94a3b8' }}>—</span>
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true,
      render: (v) => v || <span style={{ color: '#94a3b8' }}>—</span>
    }
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600 }}>
            Components of {recordLabel || 'Item Set'}
          </span>
          {set && set.setType ? (
            <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
              {String(set.setType).replace(/_/g, ' ')}
            </Tag>
          ) : null}
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button icon={<CloseOutlined />} onClick={onClose}>Close</Button>
          {onEdit ? (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => onEdit(recordId)}
            >
              Edit set
            </Button>
          ) : null}
        </div>
      }
    >
      {error ? (
        <Alert type="error" showIcon message="Failed to load" description={error} />
      ) : loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : !set ? (
        <Empty description="This item is not a set." />
      ) : (
        <>
          {set.setName ? (
            <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
              <strong>Set name:</strong> {set.setName}
            </div>
          ) : null}
          {set.remarks ? (
            <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
              <strong>Remarks:</strong> {set.remarks}
            </div>
          ) : null}
          <Space size={6} wrap style={{ marginBottom: 8 }}>
            <Tag color={set.isStockDeductAsComponents ? 'blue' : 'default'}>
              {set.isStockDeductAsComponents ? 'Stock deducts from components' : 'Stock deducts at set level'}
            </Tag>
            <Tag color={set.isPriceFromComponents ? 'blue' : 'default'}>
              {set.isPriceFromComponents ? 'Price from components' : 'Price set on parent'}
            </Tag>
          </Space>
          <Table
            size="small"
            rowKey="id"
            dataSource={components}
            columns={columns}
            pagination={false}
            locale={{ emptyText: 'No components in this set yet.' }}
          />
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12, fontSize: 11.5 }}
            message="Stock deduction from components will be applied once the Sales / Inventory posting module ships. Master data only at this stage."
          />
        </>
      )}
    </Drawer>
  );
}
